import { useFormattedSites } from '@app/ccf/company-report/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetSites } from '@shared/api';
import {
  useCreateOtherActivity,
  useDeleteOtherActivity,
  useGetOtherActivity,
  useUpdateOtherActivity,
} from '@shared/api/hooks/ccf/activities/other';
import InputFormField from '@shared/components/form/input-form-field';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import Loader from '@shared/components/loader';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { object, string, z } from 'zod';
import { useCCF } from '../../../ccf-context';

const additionalCategoryFormSchema = object({
  site: string().optional(),
  description: string({
    required_error: 'Please provide a description',
  }).min(1, 'Description is required'),
  emissions: z.coerce.number({
    required_error: 'Please provide an emissions value',
    invalid_type_error: 'Emissions must be a number',
  }),
});

interface AdditionalCategoryEditFormProps {
  activityTypeSlug: string;
  closeDialog: () => void;
  activityUuid?: string;
}

export default function AdditionalCategoryEditForm({
  activityTypeSlug,
  closeDialog,
  activityUuid,
}: AdditionalCategoryEditFormProps) {
  const { uuid: reportUuid } = useCCF();
  const { toast } = useToast();

  const { mutateAsync: createOtherActivity, isLoading: isCreating } =
    useCreateOtherActivity();
  const { mutateAsync: updateOtherActivity, isLoading: isUpdating } =
    useUpdateOtherActivity();
  const { mutateAsync: deleteOtherActivity, isLoading: isDeleting } =
    useDeleteOtherActivity(activityTypeSlug);

  const { data: activity, isLoading: isActivityLoading } = useGetOtherActivity({
    companyReportUuid: reportUuid!,
    activityTypeSlug,
    activityUuid,
  });

  const { data: sites, isLoading: isSitesLoading } = useGetSites({
    page: 1,
    page_size: 1000,
  });

  const form = useForm<z.infer<typeof additionalCategoryFormSchema>>({
    resolver: zodResolver(additionalCategoryFormSchema),
    defaultValues: {
      site: undefined,
      description: '',
      emissions: undefined,
    },
  });

  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (activity) {
      reset({
        site: activity.site?.uuid,
        description: activity.description,
        emissions: activity.emissions,
      });
    }
  }, [activity, reset]);

  const isSubmitting = isCreating || isUpdating || isDeleting;

  const onSubmit = async (
    data: z.infer<typeof additionalCategoryFormSchema>
  ) => {
    try {
      if (activityUuid) {
        await updateOtherActivity({
          companyReportUuid: reportUuid!,
          activityTypeSlug,
          activityUuid,
          data,
        });
      } else {
        await createOtherActivity({
          companyReportUuid: reportUuid!,
          activityTypeSlug,
          data,
        });
      }
      toast({
        title: `${activityUuid ? 'Updated' : 'Added'} data`,
        variant: 'success',
      });
      closeDialog();
    } catch (error) {
      sentry.log(error);
      toast({
        title: `Failed to ${activityUuid ? 'update' : 'add'} data`,
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!activityUuid) return;
    try {
      await deleteOtherActivity({
        companyReportUuid: reportUuid!,
        activityUuid,
      });
      toast({
        title: 'Deleted data',
        variant: 'success',
      });
      closeDialog();
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to delete data',
        variant: 'destructive',
      });
    }
  };

  const formattedSites = useFormattedSites(sites?.results || []);

  if (isSitesLoading || (activityUuid && isActivityLoading)) {
    return <Loader />;
  }

  return (
    <Form {...form}>
      <form className="mt-2 space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-12 gap-4">
          <SelectSearchFormField
            name="site"
            pluralName="sites"
            label="Site"
            options={formattedSites || []}
            className="col-span-4"
            placeholder="Select a site"
            optional
          />
          <InputFormField
            name="description"
            label="Description"
            className="col-span-5"
            placeholder="Describe activity & methodology"
          />
          <InputFormField
            name="emissions"
            label="Emissions"
            type="number"
            textAlign="right"
            inputClassName="text-right"
            step="any"
            adornmentRight={
              <span>
                tCO<sub>2</sub>e
              </span>
            }
            adornmentWidth={48}
            className="col-span-3"
            placeholder="0"
          />
        </div>

        <div className="mt-8 flex justify-between">
          <div className="col-span-4">
            {activityUuid && (
              <Button
                type="button"
                size="lg"
                variant="outline-destructive"
                loading={isDeleting}
                disabled={isSubmitting}
                onClick={handleDelete}
              >
                Delete data
              </Button>
            )}
          </div>
          <div className="flex space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                closeDialog();
                form.reset();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {activityUuid ? 'Update' : 'Add'} data
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
